import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ThreatMonitor from '../components/ThreatMonitor';
import AnimatedShield from '../components/AnimatedShield';
import { 
  Shield, 
  Activity, 
  Globe, 
  Lock,
  Eye,
  Wifi,
  Server,
  CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecurityDashboard() {
  const [status, setStatus] = useState(null);
  const [shieldActive, setShieldActive] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    // Trigger shield animation periodically
    const interval = setInterval(() => {
      setShieldActive(true);
      setTimeout(() => setShieldActive(false), 5000);
    }, 15000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/security/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch security status:', error);
    }
  };

  const services = [
    { id: 'tracker_blocking', name: 'Tracker Blocking', icon: Eye, description: 'Blocks ad trackers and analytics' },
    { id: 'location_protection', name: 'Location Protection', icon: Globe, description: 'Prevents location tracking' },
    { id: 'fingerprint_protection', name: 'Fingerprint Protection', icon: Activity, description: 'Blocks browser fingerprinting' },
    { id: 'vpn_recommendation', name: 'VPN Integration', icon: Wifi, description: 'Encrypted traffic routing' },
    { id: 'ai_assistant', name: 'AI Assistant', icon: Server, description: 'Privacy guidance on demand' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="security-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <AnimatedShield isActive={shieldActive} size="xl" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-white mb-2">
            Security Status
          </h1>
          <p className="text-gray-500">
            Real-time monitoring of threats and protection services
          </p>
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#0a0a0a] border-[#262626]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#00FF94]" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Shield Status</p>
                <p className="text-sm font-medium text-[#00FF94]">ACTIVE</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0a0a0a] border-[#262626]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Protection Level</p>
                <p className="text-sm font-medium text-green-400 uppercase">Maximum</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0a0a0a] border-[#262626]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Uptime</p>
                <p className="text-sm font-medium text-white">{status?.uptime_percentage || 99.97}%</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0a0a0a] border-[#262626]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Server className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Services</p>
                <p className="text-sm font-medium text-white">5/5 Online</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Threat Monitor */}
          <div>
            <ThreatMonitor />
          </div>

          {/* Services Status */}
          <div>
            <Card className="bg-[#0a0a0a] border-[#262626] h-full">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Server className="w-5 h-5 text-[#00FF94]" />
                  Protection Services
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {services.map((service) => (
                  <div 
                    key={service.id}
                    className="flex items-center justify-between p-3 bg-[#171717] rounded-lg border border-[#262626]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#00FF94]/10 flex items-center justify-center">
                        <service.icon className="w-4 h-4 text-[#00FF94]" />
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{service.name}</p>
                        <p className="text-xs text-gray-500">{service.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-green-400 font-mono">ACTIVE</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* World Map Visualization Placeholder */}
        <div className="mt-8">
          <Card className="bg-[#0a0a0a] border-[#262626]">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#00FF94]" />
                Global Threat Origins (Last 24h)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative h-[200px] bg-[#171717] rounded-lg overflow-hidden flex items-center justify-center">
                {/* Simulated dots for threat origins */}
                <div className="absolute inset-0">
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 rounded-full bg-red-500 animate-ping"
                      style={{
                        left: `${Math.random() * 80 + 10}%`,
                        top: `${Math.random() * 80 + 10}%`,
                        animationDelay: `${Math.random() * 2}s`,
                        animationDuration: `${2 + Math.random()}s`
                      }}
                    />
                  ))}
                </div>
                <p className="text-gray-500 text-sm z-10">
                  Blocking threats from 47 countries worldwide
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
